import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showSuccessToast, handleAPIError, TOAST_MESSAGES } from '@/utils/toasts';
import { APIRoutes } from '@/services/api.routes';
import { analytics } from '@/services/analytics';
import { useUser } from '@/contexts/UserContext';
import { ChatMessage } from '@/components/chat/ChatMessage';
import GeneratingResponseBubble from '@/components/chat/GeneratingResponseBubble';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import type { ChatMessage as ChatMessageType, LLMLevel } from '@/models/chat-message';
import type { ChatSession } from '@/models';
import {
  faChevronLeft,
  faMessage,
  faPaperPlane,
  faSpinner,
  faExclamationTriangle,
  faTimes,
  faBug,
  faLock,
  faImage,
  faXmark,
  faBolt
} from '@/fontawesome';

export default function ChatWindow() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { isAdmin, user } = useUser();

  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatSession, setChatSession] = useState<ChatSession | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [backgroundFetching, setBackgroundFetching] = useState(false);
  const [backgroundError, setBackgroundError] = useState<string | null>(null);
  const [debugMode, setDebugMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previousMessageCountRef = useRef<number>(0);
  const previousLlmActiveRef = useRef<boolean | undefined>(undefined);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint in Tailwind
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchNewMessages = async (afterMessageId?: string, isBackgroundFetch = false) => {
    if (!sessionId) return;

    if (isBackgroundFetch) {
      setBackgroundFetching(true);
    }

    try {
      const messagesData = await APIRoutes.listMessages(sessionId, afterMessageId, debugMode);
      const newMessages = messagesData.messages;

      // Check if chat session has actually changed
      setChatSession(prev => {
        if (prev?.llmActive !== messagesData.chatSession.llmActive ||
            prev?.closed !== messagesData.chatSession.closed ||
            prev?.title !== messagesData.chatSession.title) {
          return messagesData.chatSession;
        }
        return prev;
      });

      if (afterMessageId) {
        // Only update messages if there are actually new ones
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const uniqueNewMessages = newMessages.filter(m => !existingIds.has(m.id));

          // If no new messages, don't update state (prevents re-render)
          if (uniqueNewMessages.length === 0) {
            return prev;
          }

          return [...prev, ...uniqueNewMessages];
        });
      } else {
        // Initial load - always set messages
        setMessages(newMessages);
      }

      // Clear background error on success
      if (isBackgroundFetch) {
        setBackgroundError(null);
      }
    } catch (err) {
      if (!afterMessageId) {
        setError('Fehler beim Laden der Nachrichten');
        handleAPIError(err, TOAST_MESSAGES.MESSAGES_LOAD_ERROR);
      } else if (isBackgroundFetch) {
        setBackgroundError('Fehler beim Abrufen neuer Nachrichten');
        console.error('Error fetching messages in background:', err);
      } else {
        handleAPIError(err, TOAST_MESSAGES.MESSAGES_LOAD_ERROR);
      }
    } finally {
      if (isBackgroundFetch) {
        setBackgroundFetching(false);
      }
    }
  };

  useEffect(() => {
    if (!sessionId) {
      navigate('/chats');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        await fetchNewMessages();
      } catch (err) {
        setError('Fehler beim Laden des Chats');
        handleAPIError(err, TOAST_MESSAGES.LOADING_CHAT);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Start polling for new messages
    const getPollingInterval = () => {
      // Increase polling rate when LLM is active
      return chatSession?.llmActive ? 1000 : 3000; // 1 second when active, 3 seconds otherwise
    };

    const interval = setInterval(() => {
      const lastMessage = messages[messages.length - 1];
      fetchNewMessages(lastMessage?.id, true);
    }, getPollingInterval());

    setPollingInterval(interval);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [sessionId, navigate]);

  // Update polling when messages change
  useEffect(() => {
    if (!sessionId || !pollingInterval) return;

    clearInterval(pollingInterval);

    const getPollingInterval = () => {
      // Increase polling rate when LLM is active
      return chatSession?.llmActive ? 1000 : 3000; // 1 second when active, 3 seconds otherwise
    };

    const interval = setInterval(() => {
      const lastMessage = messages[messages.length - 1];
      fetchNewMessages(lastMessage?.id, true);
    }, getPollingInterval());

    setPollingInterval(interval);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [messages, sessionId, chatSession?.llmActive, debugMode]);

  useEffect(() => {
    const currentMessageCount = messages.length;
    const previousMessageCount = previousMessageCountRef.current;
    const currentLlmActive = chatSession?.llmActive;
    const previousLlmActive = previousLlmActiveRef.current;

    // Only scroll if:
    // 1. New messages were added (count increased)
    // 2. LLM active state changed (generating bubble appears/disappears)
    const shouldScroll =
      currentMessageCount > previousMessageCount ||
      currentLlmActive !== previousLlmActive;

    if (shouldScroll) {
      scrollToBottom();
    }

    // Update the previous values
    previousMessageCountRef.current = currentMessageCount;
    previousLlmActiveRef.current = currentLlmActive;
  }, [messages, chatSession?.llmActive]);

  // Reload messages when debug mode changes
  useEffect(() => {
    if (sessionId) {
      fetchNewMessages();
    }
  }, [debugMode]);

  const handleSubmit = async (e: React.FormEvent, llmLevel: LLMLevel = 'FULL', allowTools: boolean = true) => {
    e.preventDefault();

    if ((!inputValue.trim() && selectedImages.length === 0) || sending || !sessionId || chatSession?.llmActive) return;

    const messageContent = inputValue.trim();
    const imagesToSend = [...selectedImages];

    setInputValue('');
    setSelectedImages([]);
    setSending(true);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      // Convert images to base64
      const imagePromises = imagesToSend.map(convertFileToBase64);
      const base64Images = await Promise.all(imagePromises);

      const response = await APIRoutes.sendMessage(sessionId, {
        content: messageContent,
        images: base64Images.length > 0 ? base64Images : undefined,
        llmLevel,
        allowTools
      });

      setMessages(prev => [
        ...prev,
        response.userMessage
      ]);

      // Track message sent event
      analytics.track('Chat Message Sent', {
        sessionId,
        hasImages: imagesToSend.length > 0,
        imageCount: imagesToSend.length,
        llmLevel,
        allowTools,
      });

      // Update chat session from sendMessage response for immediate llmActive feedback
      setChatSession(response.chatSession);

      // Keep focus in textarea after sending
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    } catch (err) {
      handleAPIError(err, TOAST_MESSAGES.MESSAGE_SEND_ERROR);
      setInputValue(messageContent);
      setSelectedImages(imagesToSend);
    } finally {
      setSending(false);
    }
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Prevent submitting when LLM is active
      if (!chatSession?.llmActive) {
        handleSubmit(e, 'FULL', true);
      }
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);

    const textarea = e.target;
    textarea.style.height = 'auto';
    const scrollHeight = Math.min(textarea.scrollHeight, 200);
    textarea.style.height = scrollHeight + 'px';
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data:image/...;base64, prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const imageFiles = Array.from(files).filter(file =>
      file.type.startsWith('image/')
    );

    if (imageFiles.length !== files.length) {
      handleAPIError(new Error('Nur Bilder sind erlaubt'), 'Nur Bilder sind erlaubt');
      return;
    }

    setSelectedImages(prev => [...prev, ...imageFiles]);
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/')
    );

    if (files.length > 0) {
      setSelectedImages(prev => [...prev, ...files]);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageItems = items.filter(item => item.type.startsWith('image/'));

    if (imageItems.length > 0) {
      e.preventDefault();

      const newFiles: File[] = [];
      imageItems.forEach((item, index) => {
        const file = item.getAsFile();
        if (file) {
          // Create a new File with a proper name
          const fileName = `pasted-image-${Date.now()}-${index}.${file.type.split('/')[1]}`;
          const renamedFile = new File([file], fileName, { type: file.type });
          newFiles.push(renamedFile);
        }
      });

      if (newFiles.length > 0) {
        setSelectedImages(prev => [...prev, ...newFiles]);
      }
    }
  };

  const handleCloseChat = async () => {
    if (!sessionId || !chatSession) return;

    try {
      const updatedSession = await APIRoutes.closeChatSession(sessionId);
      setChatSession(updatedSession);
      showSuccessToast(TOAST_MESSAGES.CHAT_CLOSED);
    } catch (error) {
      handleAPIError(error, TOAST_MESSAGES.CHAT_CLOSE_ERROR);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Lade Chat..." />;
  }

  return (
    <div className="h-full flex flex-col min-h-0 relative">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl h-full flex flex-col w-full min-h-0 relative">
          {/* Session Title Bar */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center space-x-4">
              <Link
                to="/chats"
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors inline-block"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="w-6 h-6" />
              </Link>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {chatSession?.title || 'Chat'}
              </h2>
            </div>

            <div className="flex items-center space-x-2">
              {/* Close Chat Button - only visible if chat is not already closed */}
              {!chatSession?.closed && (
                <button
                  onClick={handleCloseChat}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded transition-colors cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                  title="Chat schließen"
                >
                  <FontAwesomeIcon icon={faLock} className="w-3 h-3 mr-1" />
                  SCHLIESSEN
                </button>
              )}

              {/* Debug Button - only visible to admins */}
              {isAdmin && (
                <button
                  onClick={() => setDebugMode(!debugMode)}
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded transition-colors cursor-pointer ${
                    debugMode
                      ? 'text-white bg-orange-600 hover:bg-orange-700'
                      : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:text-orange-300 dark:hover:bg-orange-900/20'
                  }`}
                  title={debugMode ? 'Debug Mode deaktivieren' : 'Debug Mode aktivieren'}
                >
                  <FontAwesomeIcon icon={faBug} className="w-3 h-3 mr-1" />
                  DEBUG
                </button>
              )}

              {/* Reserved space for refresh indicator - always present to prevent UI jumping */}
              <div className="w-4 h-4 flex items-center justify-center">
                {backgroundFetching && (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="w-4 h-4 text-gray-400 dark:text-gray-500 animate-spin opacity-30"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Background Error Banner */}
          {backgroundError && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800 px-4 py-2 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                <span className="text-sm text-yellow-800 dark:text-yellow-200">{backgroundError}</span>
              </div>
              <button
                onClick={() => setBackgroundError(null)}
                className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Foreign Chat Banner */}
          {chatSession && user && chatSession.userID !== user.id && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-4 py-3 flex items-center space-x-2 flex-shrink-0">
              <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                Dies ist ein fremder Chat - Sie können keine Nachrichten senden
              </span>
            </div>
          )}

          {/* Archived Chat Banner */}
          {chatSession?.closed && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800 px-4 py-3 flex items-center space-x-2 flex-shrink-0">
              <FontAwesomeIcon icon={faLock} className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              <div className="flex flex-col">
                <span className="text-sm text-orange-800 dark:text-orange-200 font-medium">
                  Chat wurde {chatSession.closureType === 'MANUAL' ? 'manuell' : 'automatisch'} geschlossen
                </span>
                {chatSession.closedAt && (
                  <span className="text-xs text-orange-700 dark:text-orange-300">
                    Geschlossen am: {new Date(chatSession.closedAt).toLocaleString('de-DE')}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            {messages.length === 0 && !error ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="text-gray-500 dark:text-gray-400 mb-4">
                  <FontAwesomeIcon icon={faMessage} className="w-12 h-12 mx-auto mb-4" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Starten Sie eine Unterhaltung
                </h3>
                {chatSession?.llmActive && (
                  <div className="mt-8 w-full max-w-3xl">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-4 py-3">
                      <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                        <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                        <span className="text-sm italic">Chat wird generiert...</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <ChatMessage key={message.id} message={message} debugMode={debugMode} />
                ))}
                {chatSession?.llmActive && (
                  <GeneratingResponseBubble />
                )}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input - only show if chat is not closed and not foreign */}
          {!chatSession?.closed && !(chatSession && user && chatSession.userID !== user.id) && (
            <div
              className={`border-t border-gray-200 dark:border-gray-700 p-4 flex-shrink-0 ${isDragOver ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Selected Images Preview */}
              {selectedImages.length > 0 && (
                <div className="mb-3">
                  <div className="flex flex-wrap gap-2">
                    {selectedImages.map((file, index) => (
                      <div key={index} className="relative">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Selected ${index + 1}`}
                          className="w-16 h-16 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="flex items-start space-x-3">
                {/* Image Upload Button */}
                <div className="flex-shrink-0">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    className="hidden"
                    id="image-upload"
                    disabled={sending}
                  />
                  <label
                    htmlFor="image-upload"
                    className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faImage} className="w-5 h-5" />
                  </label>
                </div>

                <div className="flex-1">
                  <textarea
                    ref={textareaRef}
                    value={inputValue}
                    onChange={handleTextareaChange}
                    onKeyDown={handleTextareaKeyDown}
                    onPaste={handlePaste}
                    placeholder={
                      isMobile
                        ? (chatSession?.llmActive ? "Bitte warten" : "Schreiben Sie...")
                        : (chatSession?.llmActive ? "Warten Sie auf die Antwort..." : "Schreiben Sie Ihre Nachricht...")
                    }
                    disabled={sending}
                    className="w-full resize-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                    style={{ minHeight: '48px', maxHeight: '200px' }}
                    rows={1}
                  />
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'NANO', false)}
                    disabled={(!inputValue.trim() && selectedImages.length === 0) || sending || chatSession?.llmActive}
                    className="bg-yellow-500 text-white rounded-lg px-4 py-3 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                    title="Schnelle Antwort - Verwendet ein kleineres, schnelleres Modell für einfache Fragen"
                  >
                    {sending ? (
                      <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 animate-spin" />
                    ) : (
                      <FontAwesomeIcon icon={faBolt} className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    type="submit"
                    disabled={(!inputValue.trim() && selectedImages.length === 0) || sending || chatSession?.llmActive}
                    className="bg-blue-600 text-white rounded-lg px-4 py-3 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                    title="Normale Antwort (mit Tools)"
                  >
                    {sending ? (
                      <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 animate-spin" />
                    ) : (
                      <FontAwesomeIcon icon={faPaperPlane} className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </form>

              {/* Drag and Drop Overlay */}
              {isDragOver && (
                <div className="absolute inset-0 bg-blue-100 dark:bg-blue-900/40 border-2 border-dashed border-blue-400 dark:border-blue-500 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <FontAwesomeIcon icon={faImage} className="w-8 h-8 text-blue-500 mb-2" />
                    <p className="text-blue-600 dark:text-blue-400 font-medium">
                      Bilder hier ablegen
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
  );
}
